const isBrowser = typeof window !== 'undefined'
const LOGIN_METHOD_KEY = 'analytics-login-method'
const RETURN_VISIT_KEY = 'analytics-last-visit'
const RETURN_VISIT_FIRED_KEY = 'analytics-return-visit-fired'
const CONSENT_STATE_KEY = 'analytics-consent-state'
const INTEREST_FLAGS_KEY = 'analytics-interest-flags'
const INTEREST_FIRED_KEY = 'analytics-interest-fired'
const INTEREST_WINDOW_MS = 1000 * 60 * 60 * 24
const INTEREST_EVENTS = ['filter_apply', 'content_view', 'file_download', 'contact_form_open']
const MAX_PAYLOAD_DEPTH = 12
const NESTED_PAYLOAD_KEYS = [
  'page',
  'user',
  'component',
  'interaction',
  'download',
  'content',
  'consent',
  'list',
  'video',
  'chart',
  'table',
  'quiz',
]
const DEFAULT_ANALYTICS_USER_CONTEXT = {
  user_id: null,
  user: {
    isLoggedIn: false,
    cognitoSub: null,
    loginMethod: null,
    accessLevel: 'anonymous',
  },
}

let analyticsUserContext = DEFAULT_ANALYTICS_USER_CONTEXT

const isPlainObject = (value) => {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

const isEmptyValue = (value) => {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (isPlainObject(value)) return Object.keys(value).length === 0
  return false
}

const cleanPayload = (value, seen = new WeakSet(), depth = 0) => {
  if (value === undefined || value === null) return value
  if (typeof value === 'function' || typeof value === 'symbol') return undefined
  if (typeof value === 'bigint') return value.toString()
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  }

  if (Array.isArray(value)) {
    if (seen.has(value) || depth >= MAX_PAYLOAD_DEPTH) return undefined
    seen.add(value)
    const cleaned = value
      .map((item) => cleanPayload(item, seen, depth + 1))
      .filter((item) => !isEmptyValue(item))
    seen.delete(value)
    return cleaned.length ? cleaned : undefined
  }

  if (isPlainObject(value)) {
    if (seen.has(value) || depth >= MAX_PAYLOAD_DEPTH) return undefined
    seen.add(value)
    const cleanedObj = {}
    Object.entries(value).forEach(([key, val]) => {
      const cleanedVal = cleanPayload(val, seen, depth + 1)
      if (!isEmptyValue(cleanedVal)) cleanedObj[key] = cleanedVal
    })
    seen.delete(value)
    return Object.keys(cleanedObj).length ? cleanedObj : undefined
  }

  if (typeof value === 'object') return undefined
  return value
}

const stripUndefinedPayload = (value, seen = new WeakSet(), depth = 0) => {
  if (value === undefined) return undefined
  if (typeof value === 'function' || typeof value === 'symbol') return undefined
  if (value === null) return null
  if (typeof value === 'bigint') return value.toString()
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  }

  if (Array.isArray(value)) {
    if (seen.has(value) || depth >= MAX_PAYLOAD_DEPTH) return undefined
    seen.add(value)
    const cleaned = value
      .map((item) => stripUndefinedPayload(item, seen, depth + 1))
      .filter((item) => item !== undefined)
    seen.delete(value)
    return cleaned
  }

  if (isPlainObject(value)) {
    if (seen.has(value) || depth >= MAX_PAYLOAD_DEPTH) return undefined
    seen.add(value)
    const cleanedObj = {}
    Object.entries(value).forEach(([key, val]) => {
      const cleanedVal = stripUndefinedPayload(val, seen, depth + 1)
      if (cleanedVal !== undefined) cleanedObj[key] = cleanedVal
    })
    seen.delete(value)
    return cleanedObj
  }

  if (typeof value === 'object') return undefined
  return value
}

const mergeNested = (base = {}, next = {}) => {
  const merged = { ...base, ...next }
  NESTED_PAYLOAD_KEYS.forEach((key) => {
    const baseValue = isPlainObject(base[key]) ? base[key] : null
    const nextValue = isPlainObject(next[key]) ? next[key] : null
    if (baseValue || nextValue) {
      merged[key] = { ...(baseValue || {}), ...(nextValue || {}) }
    }
  })
  return merged
}

const resolveTitle = ({ title, template, path }) => {
  const docTitle = isBrowser ? document.title : ''
  return title || docTitle || template || path || 'Unknown'
}

const readStorageValue = (storage, key) => {
  if (!storage || !key) return null
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

const writeStorageValue = (storage, key, value) => {
  if (!storage || !key) return
  try {
    storage.setItem(key, value)
  } catch {
    /* no-op */
  }
}

const parseJson = (raw, fallback) => {
  if (!raw) return fallback
  try {
    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

const resolveCognitoSub = (user) => {
  if (typeof user?.sub === 'string' && user.sub.trim()) return user.sub.trim()
  if (typeof user?.user_id === 'string' && user.user_id.trim()) return user.user_id.trim()
  if (typeof user?.userId === 'string' && user.userId.trim()) return user.userId.trim()
  return null
}

const shouldTrackPortfolio = (payload) => {
  const path = payload?.page?.path
  const section = payload?.page?.section
  if (section === 'Portfolio Solutions') return true
  return typeof path === 'string' && path.startsWith('/portfolio-solutions')
}

const emitDerivedEvent = (event, basePayload, extraPayload) => {
  const merged = mergeAnalyticsPayload(basePayload, extraPayload)
  pushDataLayerEvent(event, merged)
}

const maybeTrackReturnVisit = (event, payload) => {
  if (!isBrowser || event !== 'virtual_page_view') return
  if (!shouldTrackPortfolio(payload)) return
  const path = payload?.page?.path
  if (!path) return

  const localStore = window.localStorage
  const sessionStore = window.sessionStorage
  const lastVisitKey = `${RETURN_VISIT_KEY}:${path}`
  const firedKey = `${RETURN_VISIT_FIRED_KEY}:${path}`
  const now = Date.now()

  const previous = Number(readStorageValue(localStore, lastVisitKey) || 0)
  writeStorageValue(localStore, lastVisitKey, String(now))

  if (!previous || !Number.isFinite(previous)) return
  if (readStorageValue(sessionStore, firedKey)) return

  const days = Math.floor((now - previous) / (1000 * 60 * 60 * 24))
  if (!days || days < 0) return
  const thresholds = [7, 14, 30]
  const recencyDays = thresholds.find((threshold) => days <= threshold)
  if (!recencyDays) return

  writeStorageValue(sessionStore, firedKey, String(now))
  emitDerivedEvent('return_visit', payload, {
    interaction: { type: 'return_visit' },
    content: { recencyDays },
  })
}

const maybeTrackConsentState = (payload) => {
  if (!isBrowser) return
  const sessionStore = window.sessionStorage
  if (readStorageValue(sessionStore, CONSENT_STATE_KEY)) return
  const consent = payload?.consent || readConsentState()
  if (!consent) return
  writeStorageValue(sessionStore, CONSENT_STATE_KEY, String(Date.now()))
  emitDerivedEvent('consent_state', payload, {
    interaction: { type: 'consent_state' },
    consent,
  })
}

const maybeTrackInterestSignal = (event, payload) => {
  if (!isBrowser) return
  if (!shouldTrackPortfolio(payload)) return
  if (!INTEREST_EVENTS.includes(event)) return

  const sessionStore = window.sessionStorage
  const now = Date.now()
  const flags = parseJson(readStorageValue(sessionStore, INTEREST_FLAGS_KEY), {})
  flags[event] = now

  const cutoff = now - INTEREST_WINDOW_MS
  Object.entries(flags).forEach(([key, ts]) => {
    if (!ts || ts < cutoff) delete flags[key]
  })

  writeStorageValue(sessionStore, INTEREST_FLAGS_KEY, JSON.stringify(flags))

  if (readStorageValue(sessionStore, INTEREST_FIRED_KEY)) return
  const hasAll = INTEREST_EVENTS.every((step) => flags[step])
  if (!hasAll) return

  writeStorageValue(sessionStore, INTEREST_FIRED_KEY, String(now))
  emitDerivedEvent('interest_signal', payload, {
    interaction: { type: 'sequence' },
    content: { signalName: 'portfolio_solutions_engaged', signalScore: INTEREST_EVENTS.length },
  })
}

export const pushDataLayerEvent = (event, payload = {}, options = {}) => {
  if (!isBrowser) return
  window.dataLayer = window.dataLayer || []
  const includeUserContext = options.includeUserContext !== false
  const cleanedPayload = stripUndefinedPayload(payload) || {}
  const mergedPayload = includeUserContext
    ? mergeNested(analyticsUserContext, cleanedPayload)
    : cleanedPayload
  window.dataLayer.push({
    event,
    ...mergedPayload,
  })
}

export const pushAnalyticsEvent = (event, payload = {}) => {
  const cleaned = cleanPayload(payload) || {}
  pushDataLayerEvent(event, cleaned)
  maybeTrackConsentState(cleaned)
  maybeTrackReturnVisit(event, cleaned)
  maybeTrackInterestSignal(event, cleaned)
}

export const buildPageContext = ({ location, path, template, section, title } = {}) => {
  const resolvedPath =
    path ||
    location?.pathname ||
    (isBrowser ? window.location.pathname : '') ||
    ''
  const resolvedSection =
    section ||
    (typeof resolvedPath === 'string' && resolvedPath.startsWith('/portfolio-solutions')
      ? 'Portfolio Solutions'
      : undefined)
  const resolvedTitle = resolveTitle({ title, template, path: resolvedPath })
  return cleanPayload({
    path: resolvedPath,
    title: resolvedTitle,
    template,
    section: resolvedSection,
  }) || {}
}

export const setLoginMethod = (method) => {
  if (!isBrowser) return
  const normalized = typeof method === 'string' ? method.trim() : ''
  if (!normalized) return
  sessionStorage.setItem(LOGIN_METHOD_KEY, normalized)
}

export const getLoginMethod = () => {
  if (!isBrowser) return null
  const raw = sessionStorage.getItem(LOGIN_METHOD_KEY)
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null
}

export const buildUserContext = (user) => {
  const isLoggedIn = Boolean(user)
  const cognitoSub = resolveCognitoSub(user)
  const loginMethod = getLoginMethod()
  return cleanPayload({
    isLoggedIn,
    ...(isLoggedIn ? { cognitoSub, loginMethod } : null),
  }) || { isLoggedIn }
}

export const buildAnalyticsUserContext = (user, { access } = {}) => {
  const isLoggedIn = Boolean(user)
  const cognitoSub = resolveCognitoSub(user)
  const loginMethod = isLoggedIn ? getLoginMethod() : null
  const accessLevel = access?.isInstitutional
    ? 'institutional'
    : isLoggedIn
    ? 'standard'
    : 'anonymous'

  return {
    user_id: cognitoSub || null,
    user: {
      isLoggedIn,
      cognitoSub: cognitoSub || null,
      loginMethod: loginMethod || null,
      accessLevel,
    },
  }
}

export const setAnalyticsUserContext = (context = DEFAULT_ANALYTICS_USER_CONTEXT) => {
  const cleanedContext = stripUndefinedPayload(context) || {}
  analyticsUserContext = mergeNested(DEFAULT_ANALYTICS_USER_CONTEXT, cleanedContext)
  return analyticsUserContext
}

export const getAnalyticsUserContext = () => analyticsUserContext

export const readConsentState = () => {
  if (!isBrowser) return null
  const optanonGroups = window.OptanonActiveGroups || window.OnetrustActiveGroups
  if (optanonGroups) {
    return cleanPayload({
      status: 'granted',
      groups: optanonGroups,
    })
  }
  return null
}

export const mergeAnalyticsPayload = (base = {}, next = {}) => {
  const merged = mergeNested(base, next)
  return cleanPayload(merged) || {}
}
